import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const categoryArticles = useMemo(() => (slug ? getArticlesByCategory(slug) : []), [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading font-bold text-xl text-foreground-800 mb-2">分类未找到</h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${category.name} - 黄瓜网站 | 黄瓜${category.name}专业知识`}
        description={category.description}
        keywords={`黄瓜${category.name},${category.name},黄瓜网站`}
        canonicalUrl={`https://www.huanggua.site/category/${category.slug}`}
      />

      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: '/category/zhongzhi-jishu' },
            { label: category.name },
          ]}
        />
      </div>

      <main className="w-full px-4 md:px-6 py-6 md:py-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="bg-background-50 border border-background-200 rounded-lg p-4 sticky top-24">
                <h3 className="font-heading font-semibold text-foreground-800 text-sm mb-3 flex items-center gap-2">
                  <i className="ri-menu-2-line text-primary-500"></i>
                  内容分类
                </h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      <span className="flex items-center gap-2 whitespace-nowrap">
                        <i className={`${cat.icon} text-xs`}></i>
                        {cat.name}
                      </span>
                      <span className="text-xs text-foreground-400">{cat.articleCount}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <i className={`${category.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-900">
                    {category.name}
                  </h1>
                </div>
                <p className="text-sm text-foreground-500 ml-13">{category.description}</p>
              </div>

              <div className="space-y-4">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-full sm:w-56 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 p-4 md:p-5 flex flex-col justify-between">
                      <div>
                        <h2 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.summary}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {article.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="text-[10px] text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                        <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">
                          {article.author} · {article.publishDate} · {article.readTime}分钟阅读 · {article.viewCount.toLocaleString()}次浏览
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {categoryArticles.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}